/**
 * Subscription Provider Component
 * 
 * Provides subscription context and state management for the entire application.
 * Handles subscription operations, caching, and error management.
 */

import React, { useReducer, useCallback, useMemo, useEffect, useRef } from 'react';
import { SubscriptionContext, DEFAULT_SUBSCRIPTION_STATE } from '../context/SubscriptionContext';
import { subscriptionReducer, subscriptionActions } from '../reducers/subscriptionReducer';
import { 
  SUBSCRIPTION_STATUS, 
  SUBSCRIPTION_TYPES,
  SUBSCRIPTION_FEATURES,
  DEFAULT_SUBSCRIPTION_CONFIG,
  SUBSCRIPTION_STATUS_LABELS,
  SUBSCRIPTION_TYPE_LABELS
} from '../constants/subscriptionTypes';
import { useTranslation } from '../../i18n/hooks';
import { useAuth } from '../../auth/hooks';
import SubscriptionService from '../services/SubscriptionService';

/**
 * Subscription Provider Component
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @param {Object} props.initialState - Initial subscription state (optional)
 * @returns {JSX.Element} Subscription provider component
 */
export const SubscriptionProvider = ({ 
  children, 
  initialState = DEFAULT_SUBSCRIPTION_STATE 
}) => {
  // SubscriptionProvider initialized
  
  // Simple fallback translation function
  const t = (key, params = {}) => {
    // Fallback translation function
    const fallbacks = {
      'language': 'bg', // Default to Bulgarian
      'subscription.status.active': 'Active',
      'subscription.status.inactive': 'Inactive',
      'subscription.status.expired': 'Expired',
      'subscription.status.cancelled': 'Cancelled',
      'subscription.types.basic': 'Basic',
      'subscription.types.premium': 'Premium',
      'subscription.types.enterprise': 'Enterprise',
      'subscription.types.none': 'None',
      'subscription.pricing.basic': 'Free',
      'subscription.pricing.premium': '29.99 BGN/month',
      'subscription.pricing.enterprise': '99.99 BGN/month',
      'subscription.pricing.none': 'N/A',
      'subscription.dates.notSet': 'Not set',
      'subscription.dates.invalid': 'Invalid date',
      'subscription.days.remaining': 'days remaining',
      'subscription.days.overdue': 'days overdue',
      'subscription.actions.activate': 'Activate',
      'subscription.actions.activating': 'Activating...',
      'subscription.actions.changePlan': 'Change Plan',
      'subscription.actions.cancel': 'Cancel',
      'subscription.actions.cancelling': 'Cancelling...',
      'subscription.labels.status': 'Status',
      'subscription.labels.expiryDate': 'Expiry Date',
      'subscription.labels.renewalDate': 'Renewal Date',
      'subscription.labels.activatedDate': 'Activated Date',
      'subscription.labels.daysRemaining': 'Days Remaining',
      'subscription.title': 'Subscription Status'
    };
    
    let translation = fallbacks[key] || key;
    
    // Simple parameter replacement
    Object.keys(params).forEach(param => {
      translation = translation.replace(`{${param}}`, params[param]);
    });
    
    return translation;
  };
  
  // Initialize reducer with initial state
  const [state, dispatch] = useReducer(subscriptionReducer, initialState);
  const { isAuthenticated, loading: authLoading } = useAuth();
  
  // Track if initial status check has been called
  const hasCheckedStatus = useRef(false);
  const wasAuthenticated = useRef(false);

  /**
   * Check if cache is valid
   */
  const isCacheValid = useCallback(() => {
    if (!state.cache.isValid || !state.cache.timestamp) {
      return false;
    }
    
    const cacheAge = Date.now() - new Date(state.cache.timestamp).getTime();
    const isValid = cacheAge < DEFAULT_SUBSCRIPTION_CONFIG.CACHE_DURATION;
    return isValid;
  }, [state.cache]);

  /**
   * Check subscription status
   */
  const checkStatus = useCallback(async () => {
    // Prevent multiple simultaneous calls
    if (state.loading.checkStatus) {
      return { data: null, error: { message: 'Check already in progress' } };
    }
    
    try {
      dispatch(subscriptionActions.checkStatus());
      
      // Make actual API call to check subscription status
      const result = await SubscriptionService.checkStatus();
      
      if (result.error) {
        dispatch(subscriptionActions.checkStatusFailure({
          message: result.error.message || 'Failed to check subscription status',
          details: result.error
        }));
        return { data: null, error: result.error };
      }
      
      dispatch(subscriptionActions.checkStatusSuccess(result.data));
      return { data: result.data, error: null };
    } catch (error) {
      const errorObj = {
        message: error.message || 'Failed to check subscription status',
        details: error
      };
      dispatch(subscriptionActions.checkStatusFailure(errorObj));
      return { data: null, error: errorObj };
    }
  }, [state.loading.checkStatus]);

  /**
   * Activate subscription
   */
  const activate = useCallback(async () => {
    try {
      dispatch(subscriptionActions.activate());
      
      // Make actual API call to activate subscription
      const result = await SubscriptionService.activate(SUBSCRIPTION_TYPES.BASIC);
      
      if (result.error) {
        dispatch(subscriptionActions.activateFailure({
          message: result.error.message || 'Failed to activate subscription',
          details: result.error
        }));
        return { data: null, error: result.error };
      }
      
      dispatch(subscriptionActions.activateSuccess(result.data));
      return { data: result.data, error: null };
    } catch (error) {
      const errorObj = {
        message: error.message || 'Failed to activate subscription',
        details: error
      };
      dispatch(subscriptionActions.activateFailure(errorObj));
      return { data: null, error: errorObj };
    }
  }, []);

  /**
   * Deactivate subscription
   */
  const deactivate = useCallback(async () => {
    try {
      dispatch(subscriptionActions.deactivate());
      
      // TODO: Implement actual API call to deactivate subscription
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      dispatch(subscriptionActions.deactivateSuccess());
    } catch (error) {
      console.error('Failed to deactivate subscription:', error);
      dispatch(subscriptionActions.deactivateFailure({
        message: error.message || 'Failed to deactivate subscription',
        details: error
      }));
    }
  }, []);

  /**
   * Renew subscription
   */
  const renew = useCallback(async () => {
    try {
      dispatch(subscriptionActions.renew());
      
      // TODO: Implement actual API call to renew subscription
      const mockData = {
        status: SUBSCRIPTION_STATUS.ACTIVE,
        expiresAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
        renewsAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()
      };
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      dispatch(subscriptionActions.renewSuccess(mockData));
    } catch (error) {
      console.error('Failed to renew subscription:', error);
      dispatch(subscriptionActions.renewFailure({
        message: error.message || 'Failed to renew subscription',
        details: error
      }));
    }
  }, []);

  /**
   * Cancel subscription
   */
  const cancel = useCallback(async () => {
    try {
      dispatch(subscriptionActions.cancel());
      
      // Make actual API call to cancel subscription
      const result = await SubscriptionService.cancel();
      
      if (result.error) {
        dispatch(subscriptionActions.cancelFailure({
          message: result.error.message || 'Failed to cancel subscription',
          details: result.error
        }));
        return { data: null, error: result.error };
      }
      
      dispatch(subscriptionActions.cancelSuccess(result.data));
      return { data: result.data, error: null };
    } catch (error) {
      const errorObj = {
        message: error.message || 'Failed to cancel subscription',
        details: error
      };
      dispatch(subscriptionActions.cancelFailure(errorObj));
      return { data: null, error: errorObj };
    }
  }, []);

  /**
   * Refresh subscription data
   */
  const refresh = useCallback(async () => {
    try {
      dispatch(subscriptionActions.refresh());
      
      // TODO: Implement actual API call to refresh subscription data
      const mockData = {
        ...state,
        lastUpdated: new Date().toISOString()
      };
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 500));
      
      dispatch(subscriptionActions.refreshSuccess(mockData));
    } catch (error) {
      console.error('Failed to refresh subscription data:', error);
      dispatch(subscriptionActions.refreshFailure({
        message: error.message || 'Failed to refresh subscription data',
        details: error
      }));
    }
  }, [state]);

  /**
   * Clear subscription data
   */
  const clear = useCallback(() => {
    dispatch(subscriptionActions.clear());
  }, []);

  /**
   * Set loading state
   */
  const setLoading = useCallback((loading) => {
    dispatch(subscriptionActions.setLoading(loading));
  }, []);

  /**
   * Set error state
   */
  const setError = useCallback((error) => {
    dispatch(subscriptionActions.setError(error));
  }, []);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    dispatch(subscriptionActions.clearError());
  }, []);

  /**
   * Computed properties
   */
  const computed = useMemo(() => {
    const isActive = state.status === SUBSCRIPTION_STATUS.ACTIVE;
    const isExpired = state.status === SUBSCRIPTION_STATUS.EXPIRED;
    const isInactive = state.status === SUBSCRIPTION_STATUS.INACTIVE;
    
    const hasFeature = (feature) => {
      return state.features.includes(feature);
    };
    
    const daysUntilExpiry = state.expiresAt 
      ? Math.ceil((new Date(state.expiresAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
      : null;
    
    const statusLabel = state.status 
      ? (SUBSCRIPTION_STATUS_LABELS[state.status]?.[t('language')] || state.status)
      : '';
    
    const typeLabel = state.type 
      ? (SUBSCRIPTION_TYPE_LABELS[state.type]?.[t('language')] || state.type)
      : '';
    
    const needsRenewal = isExpired || (daysUntilExpiry !== null && daysUntilExpiry <= 7);
    const isInGracePeriod = isExpired && daysUntilExpiry !== null && daysUntilExpiry >= -7;
    
    return {
      isActive,
      isExpired,
      isInactive,
      hasFeature,
      daysUntilExpiry,
      statusLabel,
      typeLabel,
      needsRenewal,
      isInGracePeriod,
      nearArbEnabled: Boolean(state.nearArbEnabled),
      tier: state.tier || 'PREMIUM',
    };
  }, [state, t]);

  /**
   * Actions object
   */
  const actions = useMemo(() => ({
    checkStatus,
    activate,
    deactivate,
    renew,
    cancel,
    refresh,
    clear,
    setLoading,
    setError,
    clearError
  }), [
    checkStatus,
    activate,
    deactivate,
    renew,
    cancel,
    refresh,
    clear,
    setLoading,
    setError,
    clearError
  ]);

  /**
   * Context value
   */
  const contextValue = useMemo(() => ({
    ...state,
    actions,
    computed
  }), [state, actions, computed]);

  /**
   * Check subscription status when authenticated; reset on logout
   */
  useEffect(() => {
    if (authLoading) {
      return;
    }

    if (!isAuthenticated) {
      if (wasAuthenticated.current) {
        hasCheckedStatus.current = false;
        clear();
        SubscriptionService.clearCache();
      }
      wasAuthenticated.current = false;
      return;
    }

    wasAuthenticated.current = true;

    if (!hasCheckedStatus.current && !isCacheValid()) {
      hasCheckedStatus.current = true;
      checkStatus();
    }
  }, [isAuthenticated, authLoading, isCacheValid, checkStatus, clear]);

  /**
   * Auto-refresh subscription status for active subscribers
   */
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    const interval = setInterval(() => {
      if (state.status === SUBSCRIPTION_STATUS.ACTIVE) {
        refresh();
      }
    }, DEFAULT_SUBSCRIPTION_CONFIG.AUTO_REFRESH_INTERVAL);

    return () => clearInterval(interval);
  }, [isAuthenticated, state.status, refresh]);

  // Context value created successfully
  
  return (
    <SubscriptionContext.Provider value={contextValue}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export default SubscriptionProvider;
